import React, { useState, useEffect, useCallback } from 'react';
import { TransformationMaturity, MATURITY_TRANSFORMATION_ID } from '../../types';
import { MATURITY_PLAN_OPTIONS } from '../../constants/maturity-plans';
import TransformationTable from './TransformationTable';
import TransformationNotesModal from './TransformationNotesModal';
import { useCustomer } from '../../context/CustomerContext';
import { toast } from 'react-toastify';
import { getAllTansformationMaturity, updateTransformationMaturity } from '../../services/transformation-service';

interface TransformationTabProps {
}

const TransformationTab: React.FC<TransformationTabProps> = () => {
    const { customerData } = useCustomer();
    const [transformationData, setTransformationData] = useState<TransformationMaturity[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTransformation, setSelectedTransformation] = useState<TransformationMaturity | null>(null);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState<boolean>(false);
    const [selectedMaturityPlan, setSelectedMaturityPlan] = useState<MATURITY_TRANSFORMATION_ID>(MATURITY_TRANSFORMATION_ID.NO_MATURITY);

    const fetchTransformationData = useCallback(async () => {
        if (selectedMaturityPlan === MATURITY_TRANSFORMATION_ID.NO_MATURITY || !customerData?.customer_id) {
            setTransformationData([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await getAllTansformationMaturity(
                customerData.customer_id,
                selectedMaturityPlan
            );

            if (response && response.maturity_levels) {
                // Map the API response to match our component's expected format
                const transformedData: TransformationMaturity[] = response.maturity_levels.map((level: any) => ({
                    step_id: level.step_id,
                    transformationArea: level.transformation_area,
                    growth: level.growth,
                    notes: level.notes,
                    xaxis: level.xaxis ? parseFloat(level.xaxis) : undefined,
                    yaxis: level.yaxis ? parseFloat(level.yaxis) : undefined,
                    label_xaxis: level.label_xaxis,
                    label_yaxis: level.label_yaxis,
                    order: level.order
                }));

                setTransformationData(transformedData);
            } else {
                throw new Error('Invalid data format received from API');
            }
        } catch (err) {
            console.error('Error fetching transformation data:', err);
            setError('Failed to load transformation maturity data');
        } finally {
            setLoading(false);
        }
    }, [customerData?.customer_id, selectedMaturityPlan]);

    useEffect(() => {
        fetchTransformationData();
    }, [fetchTransformationData]);

    // Combined function to handle transformation updates
    const handleTransformationUpdate = async (
        step_id: string, 
        updateData: { growth?: string; notes?: string }
    ) => {
        try {
            // Find the item to update
            const itemToUpdate = transformationData.find(item => item.step_id === step_id);
            
            if (!itemToUpdate || !customerData?.customer_id) return;
            
            // Update local state with the provided fields
            const updatedTransformation = transformationData.map(item =>
                item.step_id === step_id ? { ...item, ...updateData } : item
            );
            setTransformationData(updatedTransformation);
            
            // Get the updated item
            const updatedItem = updatedTransformation.find(item => item.step_id === step_id);
            if (!updatedItem) return;
            
            // Update the API using the new service function
            await updateTransformationMaturity(
                customerData.customer_id,
                {
                    transformation_maturity_id: selectedMaturityPlan,
                    label: MATURITY_PLAN_OPTIONS.find(option => option.plan_id === selectedMaturityPlan)?.name || "",
                    step_id: step_id,
                    transformation_area: updatedItem.transformationArea,
                    growth: updatedItem.growth,
                    notes: updatedItem.notes || ""
                }
            );
            
            if (updateData.notes !== undefined && selectedTransformation?.step_id === step_id) {
                setIsNotesModalOpen(false);
            }
            
            toast.success('Transformation maturity data updated successfully!');
        } catch (err) {
            console.error('Error updating transformation:', err);
            setError(`Failed to update transformation: ${err instanceof Error ? err.message : 'Unknown error'}`);
            fetchTransformationData();
        }
    };

    // Simplified handler functions that use the combined update function
    const handleGrowthChange = (step_id: string, growth: string) => {
        handleTransformationUpdate(step_id, { growth });
    };

    const handleNotesClick = (transformation: TransformationMaturity) => {
        setSelectedTransformation(transformation);
        setIsNotesModalOpen(true);
    };

    const handleNotesUpdate = (notes: string) => {
        if (!selectedTransformation?.step_id) return;
        handleTransformationUpdate(selectedTransformation.step_id, { notes });
    };

    const closeNotesModal = () => {
        setIsNotesModalOpen(false);
        setSelectedTransformation(null);
    };

    return (
        <div className='px-4'>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold italic text-[#002B5C]">Transformation Maturity</h1>
            </div>

            {/* Maturity Plan Dropdown */}
            <div className="mb-6">
                <label htmlFor="maturityPlan" className="block text-sm font-medium text-gray-700 mb-2">
                    Choose A Plan
                </label>
                <div className="relative w-64">
                    <select
                        id="maturityPlan"
                        value={selectedMaturityPlan}
                        onChange={(e) => setSelectedMaturityPlan(e.target.value as MATURITY_TRANSFORMATION_ID)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md appearance-none bg-white pr-10"
                    >
                        {MATURITY_PLAN_OPTIONS.map((option) => (
                            <option key={option.plan_id} value={option.plan_id}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="mx-auto px-4">

                {selectedMaturityPlan === MATURITY_TRANSFORMATION_ID.NO_MATURITY ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>Please select a maturity plan to view the transformation maturity data.</p>
                    </div>
                ) : loading ? (
                    <div className="flex justify-center py-10 items-center h-full">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Error!</strong>
                        <span className="block sm:inline"> {error}</span>
                    </div>
                ) : (
                    <TransformationTable
                        transformationData={transformationData}
                        onGrowthChange={handleGrowthChange}
                        onNotesClick={handleNotesClick}
                    />
                )}

                {isNotesModalOpen && selectedTransformation && (
                    <TransformationNotesModal
                        transformation={selectedTransformation}
                        onClose={closeNotesModal}
                        onSave={handleNotesUpdate}
                    />
                )}
            </div>
        </div>
    );
};

export default TransformationTab;
